import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import FormLabel from '@material-ui/core/FormLabel';
import FormControl from '@material-ui/core/FormControl';
import InputAdornment from '@material-ui/core/InputAdornment';

import { Field, reduxForm, SubmissionError } from 'redux-form';

import Section, { SectionHeader, SectionTitle, SectionBody } from 'components/common/Section';
import Button from 'components/common/Button';
import TextField, { TextFieldAdornmentText } from 'components/fields/TextField';
import AutosuggestField from 'components/fields/AutosuggestField';
import RadioButtonField from 'components/fields/RadioButtonField';
import RadioGroupField from 'components/fields/RadioGroupField';
import UploadField from 'components/fields/UploadField';
import FormError from 'components/common/FormError';

import { updateUser } from 'redux/modules/users';

import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';
import objectDiff from 'utils/objectDiff';

import userTitles from 'config/userTitles';
import imageFileTypes from 'config/imageFileTypes';

const styles = theme => ({
  root: {
    marginLeft: 74,
    marginRight: 74,
  },
  grid: {
    marginBottom: 10,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 4,
    },
  },
  button: {
    marginLeft: 20,
  },
  form: {
    width: 620,
    paddingLeft: 140,
    paddingRight: 140,
    margin: '10px auto',
  },
});

function onSubmit(values, dispatch, props) {
  if (!props.pristine) {
    const diffValues = objectDiff(values, props.initialValues);
    return new Promise((resolve, reject) => {
      dispatch(updateUser({
        values: {
          ID: values.ID,
          ...diffValues,
          ...diffValues.Image && { Image: values.Image },
        },
        resolve,
        reject,
      }));
    }).then(res => res,
      (err) => {
        if (err.error) {
          throw new SubmissionError({ _error: err.error });
        }
        throw new SubmissionError(err);
      });
  }
  return values;
}

const validateForm = (values) => {
  const errors = {};

  if (values.Title && userTitles.indexOf(values.Title) === -1) {
    errors.Title = 'Please, select Title from list';
  }

  return errors;
};

const ProfileGeneralInformationEditForm = ({
  submit,
  reset,
  handleSubmit,
  submitting,
  error,
  classes,
}) => (
  <Section className={classes.root}>
    <SectionHeader>
      <SectionTitle>
        General Information
      </SectionTitle>
      <div>
        <Button
          className={classes.button}
          variant="raised"
          onClick={reset}
        >
          Reset
        </Button>
        <Button
          className={classes.button}
          variant="raised"
          color="primary"
          disabled={submitting}
          onClick={submit}
        >
          Save
        </Button>
      </div>
    </SectionHeader>
    <SectionBody>
      <form
        className={classes.form}
        noValidate
        onSubmit={handleSubmit}
        autoComplete="off"
      >
        {error &&
          <Grid
            className={classes.grid}
            container
            spacing={24}
          >
            <Grid
              item
              xs={12}
            >
              <FormError>
                {error}
              </FormError>
            </Grid>
          </Grid>
        }
        <Grid
          className={classes.grid}
          container
          spacing={24}
        >
          <Grid
            item
            xs={7}
          >
            <Field
              id="Image"
              name="Image"
              component={UploadField}
              variant="image"
              minWidth={300}
              minHeight={300}
              cropWidth={300}
              cropHeight={300}
              maxSize={1000000}
              accept={imageFileTypes}
              label={(
                <Fragment>
                  Drag file here <br />
                  JPG, PNG or GIF <br />
                  Not larger than 1 MB <br />
                  Minimum 300x300 px
                </Fragment>
              )}
              fullWidth
            />
          </Grid>
        </Grid>
        <Grid
          className={classes.grid}
          container
          spacing={24}
        >
          <Grid item xs={6}>
            <Field
              id="First Name"
              name="First Name"
              label="First Name"
              component={TextField}
              validate={[validate.required]}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <Field
              id="Last Name"
              name="Last Name"
              label="Last Name"
              component={TextField}
              validate={[validate.required]}
              fullWidth
            />
          </Grid>
        </Grid>
        <Grid
          className={classes.grid}
          container
          spacing={24}
        >
          <Grid item xs={6}>
            <Field
              id="Email"
              name="Email"
              label="Email"
              component={TextField}
              validate={[validate.required, validate.email]}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <Field
              id="Mobile Phone"
              name="Mobile Phone"
              label="Mobile Phone"
              component={TextField}
              validate={[validate.required, validate.phone]}
              format={value => (value ? value.replace('+1 ', '') : '')}
              normalize={normalize.phone}
              maxLength="14"
              startAdornment={
                <InputAdornment position="start">
                  <TextFieldAdornmentText>
                    +1
                  </TextFieldAdornmentText>
                </InputAdornment>
              }
              fullWidth
            />
          </Grid>
        </Grid>
        <Grid
          className={classes.grid}
          container
          spacing={24}
        >
          <Grid item xs={3}>
            <Field
              id="Title"
              name="Title"
              label="Title"
              component={AutosuggestField}
              validate={[validate.required]}
              placeholder="-Select Title-"
              errorPlacement="top"
              fullWidth
              renderSuggestionContent={suggestion => suggestion}
              onSuggestionSelected={suggestion => suggestion}
              fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
                if (offset === 0) {
                  if (value.length > 0) {
                    resolve(userTitles
                      .filter(item => item.toLowerCase().indexOf(value.toLowerCase()) !== -1));
                  } else {
                    resolve(userTitles);
                  }
                } else {
                  resolve([]);
                }
              }))}
            />
          </Grid>
          <Grid item>
            <FormControl role="group">
              <FormLabel component="legend">Gender</FormLabel>
              <Field
                name="Gender"
                component={RadioGroupField}
              >
                <RadioButtonField value="Male" label="Male" />
                <RadioButtonField value="Female" label="Female" />
                <RadioButtonField value="Other" label="Other" />
              </Field>
            </FormControl>
          </Grid>
        </Grid>
      </form>
    </SectionBody>
  </Section>
);

ProfileGeneralInformationEditForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  error: PropTypes.string,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    grid: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
    form: PropTypes.string.isRequired,
  }).isRequired,
};

ProfileGeneralInformationEditForm.defaultProps = {
  error: null,
};

export default reduxForm({
  onSubmit,
  validate: validateForm,
  initialValues: {
    Gender: 'Male',
  },
})(withStyles(styles)(ProfileGeneralInformationEditForm));



// WEBPACK FOOTER //
// ./src/components/account/ProfileGeneralInformationEditForm/ProfileGeneralInformationEditForm.js